import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import clsx from "clsx";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";

import TicketsManager from "../../components/TicketsManager/";
import Ticket from "../../components/Ticket/";
import AdvisorsPanel from "../../components/AdvisorsPanel";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import openSocket from "../../services/socket-io";
import api from "../../services/api";

import { i18n } from "../../translate/i18n";

const SUMMARY_REFRESH_MS = 30 * 1000;

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    flex: 1,
    height: `calc(100% - ${theme.sizing.appBar}px)`,
    overflowY: "hidden",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2),
    },
  },
  chatPapper: {
    display: "flex",
    height: "100%",
    gap: theme.spacing(2),
  },
  advisorsWrapper: {
    flex: "0 0 250px",
    height: "100%",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  ticketsWrapper: {
    flex: "0 0 380px",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflowY: "hidden",
    [theme.breakpoints.down("sm")]: {
      flex: 1,
    },
  },
  hideOnSmall: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  messagesWrapper: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    height: "100%",
    flexDirection: "column",
  },
  welcomeMsg: {
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

// Advisor counters, refreshed on chat activity and as a fallback every 30s
const useTicketsSummary = () => {
  const [summary, setSummary] = useState([]);
  const refreshTimeout = useRef();

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const { data } = await api.get("/tickets/summary");
        setSummary(data);
      } catch (err) {
        // counters are secondary; the chat list reports API errors
      }
    };

    const scheduleRefresh = () => {
      clearTimeout(refreshTimeout.current);
      refreshTimeout.current = setTimeout(fetchSummary, 1500);
    };

    void fetchSummary();
    const interval = setInterval(fetchSummary, SUMMARY_REFRESH_MS);

    const socket = openSocket();
    socket.on("connect", () => socket.emit("joinNotification"));
    socket.on("appMessage", scheduleRefresh);
    socket.on("ticket", scheduleRefresh);

    return () => {
      clearInterval(interval);
      clearTimeout(refreshTimeout.current);
      socket.disconnect();
    };
  }, []);

  return summary;
};

const Chat = () => {
  const classes = useStyles();
  const { ticketId } = useParams();
  const { whatsApps } = useContext(WhatsAppsContext);
  const location = useLocation();
  const [selectedWhatsappId, setSelectedWhatsappId] = useState(
    location.state?.whatsappId ?? null
  );
  const summary = useTicketsSummary();

  const selectedWhatsapp = whatsApps.find((w) => w.id === selectedWhatsappId);

  return (
    <div className={classes.chatContainer}>
      <div className={classes.chatPapper}>
        <div className={classes.advisorsWrapper}>
          <AdvisorsPanel
            selectedWhatsappId={selectedWhatsappId}
            onSelect={setSelectedWhatsappId}
            summary={summary}
          />
        </div>
        <div
          className={clsx(classes.ticketsWrapper, ticketId && classes.hideOnSmall)}
        >
          <TicketsManager
            selectedWhatsappId={selectedWhatsappId}
            advisorName={selectedWhatsapp?.name || i18n.t("advisors.all")}
            summary={summary}
          />
        </div>
        <div
          className={clsx(classes.messagesWrapper, !ticketId && classes.hideOnSmall)}
        >
          {ticketId ? (
            <Ticket />
          ) : (
            <Paper variant="outlined" className={classes.welcomeMsg}>
              <span>{i18n.t("chat.noTicketMessage")}</span>
            </Paper>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
